// Pie chart body — value distribution for an enum feature.
//
// Counts every checkin entry's freeform_value (where enum values land)
// and slices the pie by frequency. Slice colors come from the shared
// stable-hash assignment so an enum value keeps its color as the user
// pages through time windows (see chartColors.ts).

import React, { useEffect, useMemo, useState } from 'react';
import { PieChart, Pie, Cell, Sector, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { api } from '../../api/client';
import type { ChartBodyProps } from '../types';
import { useTimeRangeState, windowForAnchor, withinWindow } from '../windowRange';
import ChartTimeRangePicker from '../ChartTimeRangePicker';
import { assignSliceColors } from './chartColors';

interface ValueRow {
  date: string;
  freeform_value: string | null;
}

const EnumPieBody: React.FC<ChartBodyProps> = ({ featureId, config, fullscreen }) => {
  const [rows, setRows] = useState<ValueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<ValueRow[]>(`/api/features/values/${featureId}`)
      .then((resp) => { if (!cancelled) { setRows(resp.data); setError(null); } })
      .catch((err) => {
        console.error('Failed to fetch feature values:', err);
        if (!cancelled) setError('Failed to load distribution data');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [featureId]);

  const dataDates = useMemo(() => rows.map((r) => new Date(r.date).getTime()), [rows]);
  const range = useTimeRangeState(dataDates);
  const win = useMemo(
    () => windowForAnchor(range.granularity, range.anchor),
    [range.granularity, range.anchor],
  );

  const slices = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      if (!withinWindow(new Date(r.date).getTime(), win)) continue;
      const v = r.freeform_value;
      if (v == null || v === '') continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    const declared = (config?.params?.values ?? []).map(String);
    const orderedKeys: string[] = [];
    const seen = new Set<string>();
    for (const k of declared) {
      if (counts.has(k) && !seen.has(k)) { orderedKeys.push(k); seen.add(k); }
    }
    // Catch values seen in checkins that aren't in the declared set
    // (data drift after an enum edit). Append in count-desc order.
    const extras = [...counts.keys()].filter((k) => !seen.has(k))
      .sort((a, b) => (counts.get(b)! - counts.get(a)!));
    for (const k of extras) orderedKeys.push(k);

    const colors = assignSliceColors(orderedKeys);
    return orderedKeys.map((key, i) => ({
      name: key,
      value: counts.get(key) ?? 0,
      color: colors[i],
    }));
  }, [rows, config, win]);

  const total = slices.reduce((acc, s) => acc + s.value, 0);

  if (loading) {
    return (
      <div className={`flex justify-center items-center ${fullscreen ? 'h-full' : 'h-64'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }
  if (error) {
    return <div className="text-center py-8 text-rose-500"><p>{error}</p></div>;
  }
  if (rows.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No values recorded yet for this feature.</p>
      </div>
    );
  }

  return (
    <div className={fullscreen ? 'h-full flex flex-col' : 'flex flex-col'} style={{ touchAction: 'none' }}>
      <ChartTimeRangePicker state={range} />
      <div className={fullscreen ? 'flex-1 min-h-0' : 'h-80'}>
        {total === 0 ? (
          <div className="flex items-center justify-center h-full text-center text-gray-500">
            <p>No values in this period.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={slices}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="40%"
                outerRadius="75%"
                paddingAngle={1}
                isAnimationActive={true}
                activeShape={(props: {
                  cx: number; cy: number; innerRadius: number; outerRadius: number;
                  startAngle: number; endAngle: number; fill: string;
                }) => (
                  <Sector
                    cx={props.cx}
                    cy={props.cy}
                    innerRadius={props.innerRadius}
                    outerRadius={props.outerRadius}
                    startAngle={props.startAngle}
                    endAngle={props.endAngle}
                    fill={props.fill}
                    fillOpacity={0.5}
                    stroke="#ffffff"
                    strokeWidth={3}
                  />
                )}
              >
                {slices.map((s) => <Cell key={s.name} fill={s.color} style={{ outline: 'none' }} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const value = payload[0].value as number;
                  const name = payload[0].name as string;
                  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                  return (
                    <div className="bg-white border border-gray-200 rounded shadow-md px-2 py-1 text-sm text-gray-900">
                      {name}: {value} ({pct}%)
                    </div>
                  );
                }}
              />
              <Legend align="left" />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default EnumPieBody;
